import { useEffect, useState } from "react";
import { History } from "lucide-react";
import api from "../../api";

// Who changed what on the patient's profile, newest first (audit_logs via
// GET /patients/<id>/profile-history). Care team only.
const FIELD_LABELS = {
  name: "Name", dob: "Date of birth", dob_is_estimated: "Age is approximate", gender: "Gender", pronouns: "Pronouns",
  contact: "Phone", email: "Email", address: "Address", emergencyname: "Emergency contact name",
  emergencyrelation: "Emergency contact relationship", emergencyphone: "Emergency contact phone",
  medications: "Medications", conditions: "Medical conditions", allergies: "Allergies", therapyhistory: "Previous therapy",
  psychiatrichistory: "Psychiatric history", familyhistory: "Family history", substanceuse: "Substance use",
  concerns: "Presenting concerns", occupation: "Occupation", relationshipstatus: "Relationship status",
  is_active: "Active", care_team: "Care team", custom_field: "Custom field",
};
const ACTIONS = {
  create: "Created the record", update: "Edited the profile", care_team_added: "Added to the care team",
  care_team_removed: "Removed from the care team", custom_field_added: "Added a field",
  custom_field_changed: "Changed a field", custom_field_removed: "Removed a field",
};
const SHOWN = 8;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const val = (v) => (v === null || v === undefined || v === "" ? "empty" : typeof v === "boolean" ? (v ? "yes" : "no") : String(v));

export default function PatientProfileHistory({ patientId }) {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    api.getPatientProfileHistory(patientId).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [patientId]);

  const shown = all ? rows : (rows || []).slice(0, SHOWN);

  return (
    <div className="rounded-xl bg-white border border-secondary-100 p-4">
      <p className="flex items-center gap-2 text-sm font-bold text-secondary-900 mb-3"><History className="w-4 h-4 text-primary-600" /> Profile changes</p>
      {rows === null ? <p className="text-sm text-secondary-500">Loading…</p> : rows.length === 0 ? <p className="text-sm text-secondary-400">No changes recorded.</p> : (
        <>
          <ol className="relative border-l-2 border-secondary-100 ml-1.5 space-y-3">
            {shown.map((r) => {
              const fields = Object.entries(r.changes || {});
              return (
                <li key={r.id} className="pl-4">
                  <span className="absolute -left-[5px] mt-1.5 w-2 h-2 rounded-full bg-primary-400" aria-hidden="true" />
                  <p className="text-sm text-secondary-800"><span className="font-semibold">{r.by || "Someone"}</span> · {ACTIONS[r.action] || r.action}</p>
                  <p className="text-xs text-secondary-400">{fmt(r.at)}</p>
                  {r.action !== "create" && fields.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-secondary-600">
                      {fields.map(([f, c]) => (
                        <li key={f} className="break-words">
                          <span className="font-semibold">{FIELD_LABELS[f] || f}:</span>{" "}
                          {c?.old != null && c?.new != null ? <>{val(c.old)} → {val(c.new)}</> : c?.new != null ? val(c.new) : <s>{val(c?.old)}</s>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
          {rows.length > SHOWN && (
            <button type="button" onClick={() => setAll((v) => !v)} className="mt-3 text-xs font-semibold text-primary-600 hover:underline">
              {all ? "Show fewer" : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
